import React, { FC, useState, useEffect, useMemo } from 'react';

import styled from 'styled-components';

import TabsFilter from '@components/ui/listing/TabsFilter/TabsFilter';
import DateInput from '@components/date-input/DateInput';
import {
  ApplyButton,
  GuestWrapper,
  GuestBoxWrapper,
  GuestFooter,
  AddRoom,
  DynamicRow,
} from '@components/Guest/GuestBox/GuestBoxStyle';
import Guest, { GuestInfoWrapper } from '@components/Guest/Guest';
import { updateArrayWithoutMutating } from '@utils/utils';
import { FlexibleDiv } from '@components/formStyles';
import CustomIcon from '@components/utils/CustomIcon';
import { DeleteIcon } from '@assets/icons';

export { GuestInfoWrapper };
const DateInputRow = styled.div`
  &:after {
    content: '';
    display: table;
    clear: both;
  }
`;

const DateInputCol = styled.div`
  display: flex;
  flex-direction: column;
  float: left;
  width: 45%;
  margin-right: 10px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    width: 100%;
  }
`;

const BirthDateError = styled.div`
  padding-top: 5px;
  text-align: left;
  color: ${({ theme }) => theme.palette.tomato};
  font-size: 16px;
`;

export interface Guest {
  id: number;
  adultNumber: number;
  childList?: ChildDates[];
}

interface GuestBoxProps {
  onSubmit: (activeTab: number, guests: Guest[]) => void;
  guests: Guest[] | Guest;
  setGuests: React.Dispatch<React.SetStateAction<Guest[]>>;
  maxAdult?: number;
  maxChild?: number;
  maxRoom?: number;
  onTabSwitch?: (guest: Guest, activeRoomTab: number) => void;
  onTabRemoved?: (tabId: number) => void;
  isSubmitting?: boolean;
  type?: 'tour' | 'hotel';
  addRoomClick?: () => void;
  options?: React.ReactNode;
}

export interface ChildDates {
  id: number;
  date: Date | null;
  error?: boolean;
}

const GuestBoxTab: FC<GuestBoxProps> = ({
  onSubmit,
  maxAdult = 3,
  maxChild = 2,
  maxRoom = 5,
  onTabSwitch,
  onTabRemoved,
  isSubmitting,
  type = 'hotel',
  addRoomClick,
  options,
}) => {
  const [activeRoomTab, setActiveRoomTab] = useState(0);
  const [guests, setGuests] = useState<Guest[]>([{ adultNumber: 2, id: 0 }]);
  const selectedTab = guests[activeRoomTab] || guests[0];

  const guestCount = useMemo(() => {
    let totalGuests = selectedTab?.adultNumber ?? 0;
    if (selectedTab?.childList?.length) {
      totalGuests += selectedTab?.childList?.length;
    }
    return totalGuests;
  }, [selectedTab]);

  const handleChangeRoomTab = (id: string) => {
    if (addRoomClick && type === 'tour') addRoomClick();
    const selectedTabIndex = guests.findIndex(
      (guest) => guest.id === Number(id)
    );
    setActiveRoomTab(selectedTabIndex);
  };

  const handleAddTab = () => {
    if (addRoomClick && type === 'tour') addRoomClick();
    const lastIndex = guests[guests.length - 1].id;
    setGuests([...guests, { id: lastIndex + 1, adultNumber: 2 }]);
  };

  const handleRemoveTab = async (id: number) => {
    const index = guests.findIndex((guest) => guest.id === id);
    setGuests([...guests.slice(0, index), ...guests.slice(index + 1)]);
    onTabRemoved?.(id);
    setActiveRoomTab(0);
  };

  const addAdult = () => {
    const updatedObject = guests[activeRoomTab];
    setGuests(
      updateArrayWithoutMutating(
        guests,
        { ...updatedObject, adultNumber: updatedObject.adultNumber + 1 },
        selectedTab.id
      )
    );
  };

  const removeAdult = () => {
    setGuests(
      updateArrayWithoutMutating(
        guests,
        { ...selectedTab, adultNumber: selectedTab.adultNumber - 1 },
        selectedTab.id
      )
    );
  };

  const removeChild = () => {
    try {
      setGuests(
        updateArrayWithoutMutating(
          guests,
          { ...selectedTab, childList: selectedTab.childList?.slice(0, -1) },
          selectedTab.id
        )
      );
    } catch (err) {
      console.log('removeChild', err);
    }
  };

  const addChild = () => {
    const prevChilds = selectedTab.childList;
    const newChildObject = {
      id: (selectedTab.childList?.length || 0) + 1,
      date: null,
      error: false,
    };
    const updatedChilds = prevChilds?.length
      ? [...prevChilds, newChildObject]
      : [newChildObject];

    setGuests(
      updateArrayWithoutMutating(
        guests,
        {
          ...selectedTab,
          childList: updatedChilds,
        },
        selectedTab.id
      )
    );
  };

  const changeChildDate = (id: number, date: Date | null) => {
    const prevChild = selectedTab?.childList?.find((child) => child.id === id);

    if (selectedTab.childList && prevChild) {
      setGuests(
        updateArrayWithoutMutating(
          guests,
          {
            ...selectedTab,
            childList: updateArrayWithoutMutating(
              selectedTab.childList,
              { ...prevChild, date },
              id
            ),
          },
          selectedTab.id
        )
      );
    }
  };

  useEffect(() => {
    onTabSwitch?.(guests[activeRoomTab], activeRoomTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomTab, guests]);

  const errorMessage: Array<string> = ['', ''];
  const tourAdultCount =
    selectedTab.adultNumber === maxAdult || guestCount === 3;
  const adultCount = selectedTab.adultNumber === 6 || guestCount === 9;
  const tourChildCount =
    (selectedTab.childList?.length || 0) >= maxChild || guestCount === 3;
  const childCount =
    (selectedTab.childList?.length || 0) >= 3 || guestCount === 9;
  return (
    <GuestBoxWrapper>
      <TabsFilter
        activeKey={String(selectedTab?.id)}
        data={
          guests?.map((tab, index) => ({
            id: tab.id,
            label: `${index + 1}. Oda`,
          })) || []
        }
        handleTabClick={(id: string) => handleChangeRoomTab(id)}
      />

      <GuestWrapper>
        <Guest
          key='adult'
          title='Yetişkin'
          subTitle='12+ Yaş'
          counterProps={{
            min: 1,
            disabledIncrement: type === 'tour' ? tourAdultCount : adultCount,
            count: selectedTab.adultNumber,
            handleDecrement: removeAdult,
            handleIncrement: addAdult,
            tooltipMessage:
              type === 'tour' ? 'Tur en fazla 3 kişi alınabilmektedir.' : '',
          }}
        />
        <Guest
          key='child'
          title='Çocuk'
          subTitle={type === 'tour' ? '0-11 Yaş' : '2-11 Yaş'}
          counterProps={{
            count: selectedTab.childList?.length || 0,
            handleDecrement: removeChild,
            handleIncrement: addChild,
            disabledIncrement: type === 'tour' ? tourChildCount : childCount,
            disabledDecrement: false,
            tooltipMessage:
              type === 'tour' ? 'Tur en fazla 3 kişi alınabilmektedir.' : '',
          }}
        />

        <DateInputRow>
          {selectedTab.childList?.length
            ? selectedTab.childList.map((child, index) => {
                errorMessage[index] = '';
                if (type === 'tour') {
                  if (child.date === null && child.error) {
                    errorMessage[index] = 'Bu alanı boş bırakamazsınız.';
                  }
                  if (
                    Date.parse(new Date().toString()) -
                      60 * 60 * 24 * 365 * 12 * 1000 >
                    Date.parse(child.date !== null ? child.date.toString() : '')
                  ) {
                    errorMessage[index] =
                      'Çocuğun yaşı belirtilen aralığın dışındadır.';
                  }
                }
                return (
                  <DateInputCol key={child.id}>
                    <div>
                      <DateInput
                        dateFormat='dd/MM/yyyy'
                        label={`${index + 1}. Çocuk Doğum Tarihi`}
                        placeholder='DD/MM/YYYY'
                        date={child.date}
                        setDate={(e) => changeChildDate(child.id, e)}
                      />
                    </div>
                    {errorMessage[index] && (
                      <BirthDateError>{errorMessage[index]}</BirthDateError>
                    )}
                  </DateInputCol>
                );
              })
            : null}
        </DateInputRow>
      </GuestWrapper>
      {options}
      <GuestFooter alignRight>
        {guests.length > 1 ? (
          <AddRoom onClick={() => handleRemoveTab(selectedTab.id)}>
            Odayı Sil
            <FlexibleDiv left={2} top={-1}>
              <CustomIcon icon={DeleteIcon} fontSize={12} />
            </FlexibleDiv>
          </AddRoom>
        ) : null}

        <DynamicRow style={{ gap: 6 }}>
          <ApplyButton
            disabled={guests.length >= maxRoom}
            outline
            onClick={handleAddTab}
          >
            Oda Ekle
          </ApplyButton>
        </DynamicRow>
        <ApplyButton
          onClick={() => onSubmit(activeRoomTab, guests)}
          loading={isSubmitting}
          disabled={errorMessage[0] !== '' || errorMessage[1] !== ''}
        >
          Uygula
        </ApplyButton>
      </GuestFooter>
    </GuestBoxWrapper>
  );
};

export default GuestBoxTab;
